import { configureStore } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage"; // defaults to localStorage
import { persistReducer, persistStore, REHYDRATE } from "redux-persist";
import { combineReducers } from "redux";
import searchReducer from "./searchSlice";
import yachtReducer from "./yachtSlice";
import comparatorReducer from "./comparatorSlice";

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["search", "comparator"],
};

const rootReducer = combineReducers({
  search: searchReducer,
  yacht: yachtReducer,
  comparator: comparatorReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export const persistor = persistStore(store);

// Sync comparator across browser tabs: when another tab writes to localStorage,
// dispatch REHYDRATE so this tab's store picks up the latest comparator list.
// Guard: only update if the incoming data is actually newer (more items or different set)
// to prevent a stale localStorage write from reverting in-flight state changes.
window.addEventListener('storage', (event) => {
  if (event.key === 'persist:root' && event.newValue) {
    try {
      const root = JSON.parse(event.newValue);
      const incoming = root.comparator ? JSON.parse(root.comparator) : undefined;
      if (!incoming) return;
      const current = store.getState().comparator;
      // Skip if the serialised items are identical — nothing to sync
      if (JSON.stringify(incoming.items) === JSON.stringify(current.items)) return;
      store.dispatch({ type: REHYDRATE, key: 'root', payload: { comparator: incoming } });
    } catch {}
  }
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
